// NEXUS360 build 20260511
import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Navigation } from './components/Navigation';
import { LanguageSwitcher } from './components/LanguageSwitcher';
import { Login } from './pages/Login';
import { Welcome } from './pages/Welcome';
import { ExecutiveDashboard } from './pages/dashboards/ExecutiveDashboard';
import { FinanceDashboard } from './pages/dashboards/FinanceDashboard';
import { FinanceIntelligencePage } from './pages/finance/FinanceIntelligencePage';
import { FinanceDashboardV2 } from './pages/finance/FinanceDashboardV2';
import { FinanceAdvancedTabs } from './pages/finance/FinanceAdvancedTabs';
import { RHDashboard } from './pages/dashboards/RHDashboard';
import { HRIntelligencePage } from './pages/hr/HRIntelligencePage';
import { HRIntelligenceV2 } from './pages/hr/HRIntelligenceV2';
import { HRAdvancedTabs } from './pages/hr/HRAdvancedTabs';
import { OperationsDashboard } from './pages/dashboards/OperationsDashboard';
import { SmartVisionPage } from './pages/operations/SmartVisionPage';
import { ExecutiveDashboardPage } from './pages/executive/ExecutiveDashboardPage';
import { InstitutionalReportingPage } from './pages/reporting/InstitutionalReportingPage';
import { LeanAnalyticsPage } from './pages/lean/LeanAnalyticsPage';
import { TrainingAnalyticsPage } from './pages/formation/TrainingAnalyticsPage';
import { TrainingAdvancedTabs } from './pages/formation/TrainingAdvancedTabs';
import { IncidentsAnalyticsPage } from './pages/operations/IncidentsAnalyticsPage';
import { LogistiqueDashboard } from './pages/dashboards/LogistiqueDashboard';
import { FormationDashboard } from './pages/dashboards/FormationDashboard';
import { LeanDashboard } from './pages/dashboards/LeanDashboard';
import { FormPage } from './pages/FormPage';
import { AssetFormPage } from './pages/logistics/AssetFormPage';
import { LogisticsManagementPage } from './pages/logistics/LogisticsManagementPage';
import { LogisticsIntelligencePage } from './pages/logistics/LogisticsIntelligencePage';
import { LogisticsAdvancedTabs } from './pages/logistics/LogisticsAdvancedTabs';
import { AssetHistoryPage } from './pages/logistics/AssetHistoryPage';
import { IncidentFormPage } from './pages/incidents/IncidentFormPage';
import { IncidentBoardPage } from './pages/incidents/IncidentBoardPage';
import { IncidentHistoryPage } from './pages/incidents/IncidentHistoryPage';

const APP_VERSION = 'v3';

// ── Role-based route guard ───────────────────────────────────────────────────
const ProtectedRoute = ({ user, allowed, children }: { user: any; allowed: string[]; children: React.ReactNode }) => {
  const role = (user?.role || '').toUpperCase();
  if (!allowed.includes(role)) return <Navigate to="/welcome" replace />;
  return <>{children}</>;
};

const useClockWeather = () => {
  const [time, setTime] = React.useState(new Date());
  const [weather, setWeather] = React.useState(null);
  React.useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  React.useEffect(() => {
    const icons = {'0':'☀️','1':'🌤','2':'⛅','3':'☁️','45':'🌫','51':'🌦','61':'🌧','65':'🌧','71':'🌨','80':'🌦','95':'⛈'};
    navigator.geolocation?.getCurrentPosition(async pos => {
      try {
        const { latitude: lat, longitude: lon } = pos.coords;
        const r = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weathercode&daily=temperature_2m_max,temperature_2m_min&timezone=auto&forecast_days=1`);
        const d = await r.json();
        const cr = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`);
        const cd = await cr.json();
        const city = cd.address?.city || cd.address?.town || cd.address?.village || cd.address?.county || '';
        const code = String(d.current?.weathercode ?? 0);
        setWeather({ city, temp: Math.round(d.current?.temperature_2m ?? 0), code, icon: icons[code]||'🌡️' });
      } catch {
        setWeather({ city: '', temp: 26, code: '2', icon: '⛅' });
      }
    }, () => {
      setWeather({ city: '', temp: 26, code: '2', icon: '⛅' });
    });
  }, []);
  return { time, weather };
};

const AppHeader = ({ user, sidebarCollapsed }) => {
  const { t } = useTranslation();
  const userName = user?.name || user?.username || 'Utilisateur';
  const userRole = user?.role || '';
  const { time, weather } = useClockWeather();
  const days = (t('header.days', { returnObjects: true }) as string[]) || ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const months = (t('header.months', { returnObjects: true }) as string[]) || ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const weatherDescs = (t('header.weather_desc', { returnObjects: true }) as Record<string,string>) || {};
  const timeStr = time.toLocaleTimeString('fr-FR', {hour:'2-digit', minute:'2-digit', second:'2-digit'});
  const dateStr = time.getDate() + ' ' + (months[time.getMonth()]||'') + ' ' + time.getFullYear();
  const weatherDesc = weather ? (weatherDescs[weather.code] || '') : '';
  return (
    <div className={`fixed top-0 right-0 left-0 md:transition-all md:duration-300 ${sidebarCollapsed ? 'md:left-20' : 'md:left-72'} h-16 bg-navy-900/95 backdrop-blur-sm border-b border-navy-700 z-40 flex items-center px-4 md:px-6 gap-3`}>
      <div className="w-10 md:hidden" />
      <div className="flex-none md:w-48">
        <p className="text-gold-500 text-xs md:text-sm font-semibold truncate">
          {t('welcome.greeting')} {userName}
        </p>
      </div>
      <div className="flex-1 text-center hidden md:flex md:justify-center">
        <h1 className="text-xl font-bold text-gold-500 whitespace-nowrap">
          DGSN - {t('header.title')}
        </h1>
      </div>
      <div className="hidden md:flex items-center gap-3 flex-shrink-0">
        <div className="flex flex-col items-end gap-0.5">
          <div className="flex items-center gap-2">
            {weather && (
              <>
                <span className="text-base leading-none">{weather.icon}</span>
                <span className="text-white font-semibold text-xs tabular-nums">{weather.temp}°C</span>
                {weather.city && (
                  <span className="flex items-center gap-0.5 text-slate-300 text-xs">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" className="text-yellow-500 flex-shrink-0">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                    {weather.city}
                  </span>
                )}
                <span className="text-slate-600 text-xs">·</span>
              </>
            )}
            <span className="text-white font-semibold text-xs tabular-nums">{timeStr}</span>
          </div>
          <div className="flex items-center gap-1.5 text-slate-400" style={{fontSize:'10px'}}>
            {weather?.city && <span>{weatherDesc}</span>}
            {weather?.city && <span>·</span>}
            <span className="tabular-nums">{dateStr}</span>
          </div>
        </div>
        <div className="w-px h-7 bg-navy-600"/>
      </div>
      <div className="flex-shrink-0">
        <LanguageSwitcher />
      </div>
    </div>
  );
};


// Finance Advanced tab wrapper
const FinanceAdvancedWrapper = ({ user }: { user: any }) => (
  <div className="p-4 md:p-6 space-y-4">
    <div className="flex items-center gap-2 mb-2">
      <span className="text-2xl font-bold text-white">Suivi Financier Avance</span>
      <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-green-500/20 text-green-300 border border-green-500/30">LIVE</span>
    </div>
    <FinanceAdvancedTabs raw={[]} />
  </div>
);


// HR Advanced wrapper
const HRAdvancedWrapper = ({ user }: { user: any }) => (
  <div className="p-4 md:p-6 space-y-4">
    <div className="flex items-center gap-2 mb-2">
      <span className="text-2xl font-bold text-white">Suivi RH Avancé</span>
      <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-green-500/20 text-green-300 border border-green-500/30">LIVE</span>
    </div>
    <HRAdvancedTabs />
  </div>
);


// Training Advanced wrapper
const TrainingAdvancedWrapper = ({ user }: { user: any }) => (
  <div className="p-4 md:p-6 space-y-4">
    <div className="flex items-center gap-2 mb-2">
      <span className="text-2xl font-bold text-white">Suivi Formation Avancé</span>
      <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-green-500/20 text-green-300 border border-green-500/30">LIVE</span>
    </div>
    <TrainingAdvancedTabs />
  </div>
);


// Logistics Advanced wrapper
const LogisticsAdvancedWrapper = ({ user }: { user: any }) => (
  <div className="p-4 md:p-6 space-y-4">
    <div className="flex items-center gap-2 mb-2">
      <span className="text-2xl font-bold text-white">Surveillance Flotte IA</span>
      <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-green-500/20 text-green-300 border border-green-500/30">LIVE</span>
    </div>
    <LogisticsAdvancedTabs />
  </div>
);

function App() {
  const [user, setUser] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { i18n } = useTranslation();

  useEffect(() => {
    const storedVersion = localStorage.getItem('app_version');
    if (storedVersion !== APP_VERSION) {
      localStorage.clear();
      localStorage.setItem('app_version', APP_VERSION);
    }
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      if (parsed?.role) setUser(parsed);
      else localStorage.removeItem('user');
    }
    const savedLang = localStorage.getItem('i18nextLng');
    if (savedLang) i18n.changeLanguage(savedLang);
  }, []);

  if (!user) return <Login onLogin={setUser} />;

  return (
    <div className="min-h-screen bg-navy-950">
      <Navigation
        user={user}
        onLogout={() => { localStorage.clear(); setUser(null); }}
        isCollapsed={sidebarCollapsed}
        onCollapse={setSidebarCollapsed}
      />
      <AppHeader user={user} sidebarCollapsed={sidebarCollapsed} />
      <main className={`ml-0 md:transition-all md:duration-300 ${sidebarCollapsed ? 'md:ml-20' : 'md:ml-72'} pt-16`}>
        <Routes>
          <Route path="/" element={<Navigate to="/welcome" />} />
          <Route path="/welcome" element={<Welcome user={user} />} />

          {/* Executive */}

          {/* Finance */}
          <Route path="/finance" element={<Navigate to="/finance/dashboard" />} />
          <Route path="/finance/dashboard" element={<FinanceDashboard user={user} />} />
          <Route path="/finance/intelligence" element={<FinanceDashboardV2 user={user} />} />
          <Route path="/finance/form"          element={<FormPage moduleId="finance" user={user} />} />
          <Route path="/finance/advanced"       element={<FinanceAdvancedWrapper user={user} />} />

          {/* RH */}
          <Route path="/rh" element={<Navigate to="/rh/dashboard" />} />
          <Route path="/rh/dashboard" element={<RHDashboard user={user} />} />
          <Route path="/rh/intelligence" element={<HRIntelligenceV2 user={user} />} />
          <Route path="/rh/form"     element={<FormPage moduleId="hr" user={user} />} />
          <Route path="/rh/advanced"  element={<HRAdvancedWrapper user={user} />} />

          {/* Operations */}
          <Route path="/operations" element={<Navigate to="/operations/dashboard" />} />
          <Route path="/operations/dashboard" element={<OperationsDashboard user={user} />} />
          <Route path="/operations/incidents" element={<IncidentBoardPage user={user} />} />
          <Route path="/operations/incidents/report" element={<IncidentFormPage user={user} />} />
          <Route path="/operations/history" element={<IncidentHistoryPage user={user} />} />
          <Route path="/operations/smartvision" element={<SmartVisionPage user={user} />} />
          <Route path="/operations/incidents/analytics" element={<IncidentsAnalyticsPage user={user} />} />

          {/* Logistique */}
          <Route path="/logistique" element={<Navigate to="/logistique/dashboard" />} />
          <Route path="/logistique/intelligence" element={<LogisticsIntelligencePage user={user} />} />
          <Route path="/logistique/advanced"     element={<LogisticsAdvancedWrapper user={user} />} />
          <Route path="/logistique/dashboard" element={<LogistiqueDashboard user={user} />} />
          <Route path="/logistique/assets"  element={<AssetFormPage user={user} />} />
          <Route path="/logistique/history" element={<AssetHistoryPage user={user} />} />
          <Route path="/logistique/gestion" element={<LogisticsManagementPage user={user} />} />

          {/* Formation */}
          <Route path="/formation" element={<Navigate to="/formation/dashboard" />} />
          <Route path="/executive" element={<ProtectedRoute user={user} allowed={['DG','SUPER_ADMIN']}><ExecutiveDashboardPage user={user} /></ProtectedRoute>} />
          <Route path="/reporting" element={<Navigate to="/reporting/institutional" />} />
          <Route path="/reporting" element={<Navigate to="/reporting/institutional" />} />
          <Route path="/reporting" element={<Navigate to="/reporting/institutional" />} />
          <Route path="/reporting/institutional" element={<ProtectedRoute user={user} allowed={['DG','SUPER_ADMIN','COMMISSAIRE']}><InstitutionalReportingPage user={user} /></ProtectedRoute>} />
          <Route path="/lean/analytics" element={<LeanAnalyticsPage user={user} />} />
          <Route path="/formation/analytics" element={<TrainingAnalyticsPage user={user} />} />
          <Route path="/formation/dashboard" element={<FormationDashboard user={user} />} />
          <Route path="/formation/form"     element={<FormPage moduleId="formation" user={user} />} />
          <Route path="/formation/advanced"  element={<TrainingAdvancedWrapper user={user} />} />

          {/* LEAN */}
          <Route path="/lean" element={<Navigate to="/lean/dashboard" />} />
          <Route path="/lean/dashboard" element={<LeanDashboard user={user} />} />
          <Route path="/lean/form" element={<FormPage moduleId="lean" user={user} />} />
        </Routes>
      </main>
    </div>
  );
}

export default App;
// 1779663217
